import {useLocale} from '@aboutyou/i18n';
import {lazy, useSuspenseSafeHydrationDone} from '@aboutyou/lazy-imports';
import {LoadingPageSpinner} from '@aboutyou/ui-components';
import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react';
import {InfoBanner} from '../../../client-services/Login/InfoBanner/InfoBanner';
import {useSavedUser} from '../AuthenticationModal/hooks/useSavedUser';
import {useAuthenticationOpenState} from '../hooks/useAuthenticationOpenState';
import {useIsAuthenticated} from '../hooks/useIsAuthenticated';
import {AuthMethod} from '../types';
import {getShowLoginAsDefault} from '../utility/getShowLoginAsDefault';

const AuthenticationModalLazy = lazy(
  () =>
    import('../AuthenticationModal/AuthenticationModal').then(mod => ({
      default: mod.AuthenticationModal,
    })),
  {
    fallback: <LoadingPageSpinner />,
  },
);

const AuthenticationValidatorLazy = lazy(() =>
  import('../AuthenticationValidator/AuthenticationValidator').then(mod => ({
    default: mod.AuthenticationValidator,
  })),
);

export interface AuthenticationProps {
  /**
   * Called regardless of action type, runs whenever the user successfully exists the auth prompt
   */
  onSuccess?: VoidFunction;
  /**
   * Called if an error is thrown during the authentication process
   */
  onError?: (error: string) => void;
  /**
   * Called if the user cancels the auth process e.g. closes the modal
   */
  onCancel?: VoidFunction;
  /**
   * If true, the modal will display the guest option
   */
  allowGuestLogin?: boolean;

  /**
   * If provided, it will first verify if the CO session is valid.
   * If not, the modal will be shown
   */
  includeCheckoutSession: boolean;
  /**
   * If provided, it will replace the current history entry when the modal opens
   * If not, a new entry will be added
   */
  replaceHistory?: boolean;
  /**
   * If provided, it will display login screen with the provided component at the top below the header
   */
  customBannerComponent?: React.ReactNode;
  /**
   * If provided, it will display login screen with the provided component at the top below the header
   */
  aboveHeaderBannerComponent?: React.ReactNode;
  /**
   * Disable toast shown on success. There are some situations where we dont want to show toast. Mainly mobile implementations of features
   */
  disableSuccessToast?: boolean;
  /**
   * redirect to checkout if apple login
   */
  forceRedirectToCheckoutOnAppleLoginSuccess?: boolean;
}

interface AuthenticationConfig {
  showUserAuthenticationPrompt: (props: AuthenticationProps) => void;
}

const AuthenticationContext = createContext<AuthenticationConfig>(
  {} as AuthenticationConfig,
);

export const useAuthenticationPrompt = () => {
  const {showUserAuthenticationPrompt} = useContext(AuthenticationContext);

  if (!showUserAuthenticationPrompt) {
    console.warn(
      'Cannot find value for AuthenticationContext. Did you add a AuthenticationContext in one of the parent components?',
    );
    return {showUserAuthenticationPrompt: () => {}};
  }

  return {showUserAuthenticationPrompt} as AuthenticationConfig;
};

export const AuthenticationProvider = ({
  children,
  onSyncLegacy,
}: PropsWithChildren<{onSyncLegacy?: VoidFunction}>) => {
  return (
    <AuthenticationProviderBase onSyncLegacy={onSyncLegacy}>
      {children}
    </AuthenticationProviderBase>
  );
};

const isAuthMethod = (parameter: any): parameter is AuthMethod =>
  parameter === 'register' ||
  parameter === 'login' ||
  parameter === 'guest-register';

const AuthenticationProviderBase = ({
  children,
  onSyncLegacy,
}: PropsWithChildren<{onSyncLegacy?: VoidFunction}>) => {
  const isHydrationDone = useSuspenseSafeHydrationDone();
  const onCancelRef = useRef<VoidFunction>();
  const onSuccessRef = useRef<VoidFunction>();
  const onErrorRef = useRef<(e: string) => void>();
  const allowGuestLoginRef = useRef<boolean>();
  const customBannerComponentRef = useRef<React.ReactNode>();
  const aboveHeaderBannerComponentRef = useRef<React.ReactNode>();
  const disableSuccessToastRef = useRef<boolean>(false);
  const forceRedirectToCheckoutOnAppleLoginSuccessRef = useRef<boolean>(false);

  const {isAuthenticated, isAuthenticatedCallback, isUserDataLoading} =
    useIsAuthenticated();
  const {isOpen, cancel, open, parameter, hide} = useAuthenticationOpenState({
    // We only want to enable the auto-open of the modal if the user is not authenticated
    enableParams: !isAuthenticated && !isUserDataLoading,
  });
  const {hasSavedUser, ...savedUser} = useSavedUser();
  const locale = useLocale();

  const onCancel = () => {
    cancel();
    onCancelRef.current?.();
  };

  const onSuccess = () => {
    hide();
    onSuccessRef.current?.();
    onSyncLegacy?.();
  };

  const showUserAuthenticationPrompt = useCallback(
    async ({
      onCancel,
      onError,
      onSuccess,
      allowGuestLogin,
      includeCheckoutSession,
      replaceHistory,
      customBannerComponent,
      aboveHeaderBannerComponent,
      disableSuccessToast,
      forceRedirectToCheckoutOnAppleLoginSuccess = false,
    }: AuthenticationProps) => {
      const isAuthenticated = await isAuthenticatedCallback({
        includeCheckoutSession,
      });
      // Do not open the modal if the user is already authenticated, when we call the prompt to open
      // If already authenticated, trigger the onSuccess callback if there is one
      if (isAuthenticated) {
        onSuccess?.();
        return;
      }

      onCancelRef.current = onCancel;
      onSuccessRef.current = onSuccess;
      onErrorRef.current = onError;
      allowGuestLoginRef.current = allowGuestLogin;
      customBannerComponentRef.current = customBannerComponent;
      aboveHeaderBannerComponentRef.current = aboveHeaderBannerComponent;
      disableSuccessToastRef.current = Boolean(disableSuccessToast);
      forceRedirectToCheckoutOnAppleLoginSuccessRef.current =
        forceRedirectToCheckoutOnAppleLoginSuccess;
      const shouldShowLoginAsDefault = getShowLoginAsDefault(locale);
      open({
        replaceHistory,
        parameter: shouldShowLoginAsDefault ? undefined : 'register',
      });
    },
    [isAuthenticatedCallback, locale, open],
  );

  const onAuthenticationMethodChange = (type: AuthMethod) => {
    open({replaceHistory: true, parameter: type});
  };

  const value = useMemo(
    () => ({showUserAuthenticationPrompt}),
    [showUserAuthenticationPrompt],
  );

  return (
    <AuthenticationContext.Provider value={value}>
      {children}
      {isHydrationDone && (
        <AuthenticationModalLazy
          allowGuestLogin={allowGuestLoginRef.current}
          onError={onErrorRef.current}
          onSuccess={onSuccess}
          onCancel={onCancel}
          onClickTab={onAuthenticationMethodChange}
          activeTab={isAuthMethod(parameter) ? parameter : 'login'}
          lastSeenUser={hasSavedUser ? savedUser : undefined}
          bannerComponent={customBannerComponentRef.current ?? <InfoBanner />}
          aboveHeaderBannerComponent={aboveHeaderBannerComponentRef.current}
          isOpen={isOpen}
          disableSuccessToast={disableSuccessToastRef.current}
          forceRedirectToCheckoutOnAppleLoginSuccess={
            forceRedirectToCheckoutOnAppleLoginSuccessRef.current
          }
        />
      )}
      {isAuthenticated && <AuthenticationValidatorLazy />}
    </AuthenticationContext.Provider>
  );
};
